import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Collapse, Drawer, Grid, Group, Modal, Space, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { useCreateAssetMutation, useDeleteAssetMutation, useGetAssetByIdQuery, useGetAssetsQuery, useUpdateAssetMutation } from "../../api/asset.api";
import { AssetDto, ListAssetDto, MutableAssetDto } from "../../models/assets.models";
import { AssetList } from "../../Components/Assets/AssetList";
import { AssetDetailView } from "../../Components/Assets/AssetDetailView";
import AssetEditView from "../../Components/Assets/AssetEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const AssetsPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ assetId?: string }>();
	const isEditing = useMatch("/assets/:assetId/edit") !== null;
	const isNew = params.assetId === "new";
	const assetIdFromParams = params.assetId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const { data: assets, isPending: isLoadingAssets } = useGetAssetsQuery();
	const [selectedAsset, setSelectedAsset] = useState<ListAssetDto | null>(null);
	const [parentAssetFilter, setParentAssetFilter] = useState<ListAssetDto | null>(null);
	const { data: asset, isLoading } = useGetAssetByIdQuery(selectedAsset?.AssetId || assetIdFromParams || null);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedAssetId, setRequestedAssetId] = useState<string | null>(null);

	const { mutate: createAsset, isPending: isCreating } = useCreateAssetMutation();
	const { mutate: updateAsset, isPending: isUpdating } = useUpdateAssetMutation();
	const { mutate: deleteAsset } = useDeleteAssetMutation();

	const isSaving = isCreating || isUpdating;

	useEffect(() => {
		if (assetIdFromParams) {
			const current = assets?.find((item: ListAssetDto) => item.AssetId === assetIdFromParams);
			if (current) {
				setSelectedAsset(current);
				if (isMobile) {
					openDetailViewDrawer();
				}
			}
		} else if (assets && assets.length > 0) {
			setSelectedAsset(assets[0]);
		}
	}, [assetIdFromParams, assets, isMobile]);

	useEffect(() => {
		if (!assetIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleEdit = () => {
		navigate(`/assets/${asset?.AssetId}/edit`);
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedAsset(null);
		navigate(`/assets/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleAssetSelect = (asset: ListAssetDto | null) => {
		if (!asset) return;

		setRequestedAssetId(asset.AssetId);
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}

		setSelectedAsset(asset);
		navigate(`/assets/${asset.AssetId}`);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSetParentAssetFilter = (asset: ListAssetDto | null) => {
		setParentAssetFilter(asset);
	};

	const handleClearParentAssetFilter = () => {
		setParentAssetFilter(null);
	};

	const handleSave = (values: MutableAssetDto, files: File[]) => {
		if (isNew) {
			createAsset({ asset: values, files }, {
				onSuccess: (response) => {
					setIsChanged(false);
					navigate(`/assets/${response.data.AssetId}`, { replace: true });
				}
			});
		} else {
			updateAsset({ asset: values, files }, {
				onSuccess: () => {
					setIsChanged(false);
					navigate(-1);
				}
			});
		}
	};

	const handleDelete = () => {
		if (!asset) return;

		closeDeleteModal();
		deleteAsset(asset, {
			onSuccess: () => {
				setSelectedAsset(null);
				navigate(`/assets`, { replace: true });
			}
		});
	};

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			setIsChanged(false);
			navigate(-1);

			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		setIsChanged(false);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}

		if (requestedAssetId && requestedAssetId !== asset?.AssetId) {
			navigate(`/assets/${requestedAssetId}`);
			setRequestedAssetId(null);
		} else {
			navigate(-1);
		}
	};

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	};

	return (
		<>
			<DeleteAssetModal
				asset={asset}
				open={deleteModalOpen}
				onDelete={handleDelete}
				onClose={closeDeleteModal}
			/>

			<DiscardChangesModal
				open={discardChangesModalOpened}
				onDiscard={handleDiscardChanges}
				onClose={handleCancelDiscard}
			/>

			<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
				{isEditing || isNew ? (
					<AssetEditView asset={!isNew ? asset : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				) : (
					<AssetDetailView asset={asset} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
				)}
			</Drawer>

			<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
				{SearchBox}
			</Collapse>

			<Group justify="space-between">
				<Title order={2}>Assets</Title>
				<RoleTemplate role={AzureADRoles.Admin}>
					<Button onClick={handleNew} radius="xl" leftSection={<IconPlus />}>New Asset</Button>
				</RoleTemplate>
			</Group>

			<Grid mt="lg" gutter="md">
				<Grid.Col span={{ base: 12, xs: ((!assets || assets.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
					<AssetList assets={assets} isLoading={isLoadingAssets} selectedAsset={selectedAsset} setSelectedAsset={handleAssetSelect} parentAssetFilter={parentAssetFilter} onClearParentFilter={handleClearParentAssetFilter} onSetParentFilter={handleSetParentAssetFilter} />
				</Grid.Col>
				<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
					{isEditing || isNew ? (
						<AssetEditView asset={!isNew ? asset : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
					) : assets && assets.length > 0 ? (
						<AssetDetailView asset={asset} isLoading={isLoading} onEdit={handleEdit} onDelete={openDeleteModal} />
					) : <></>}
				</Grid.Col>
			</Grid>
		</>
	);
};

export default AssetsPage;

const DiscardChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you'll lose unsaved changes to this asset.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteAssetModal = ({ asset, open, onDelete, onClose }: { asset?: AssetDto, open: boolean, onDelete: () => void, onClose: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete Asset</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>Are you sure you want to delete this asset?</Text>
			<Space h="xs" />
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
			</Group>
		</Stack>
	</Modal>
);
